import { useEffect, useRef, useState } from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { vyhodnotMereni, parseCislo, fmt } from './mereniKontrola';
import ZnamenkoTlacitko, { muzeZaporne } from '../../components/ui/ZnamenkoTlacitko';

/**
 * Nápověda pod číslem měření — ikona podle výsledku, text, u překlepu tlačítko
 * „Opravit na …" a malá vizualizace: pásmo limitu se značkou (rozmezí), nebo
 * změna od minula proti běžné a nejvyšší rozumné změně (roste / klesá).
 */
export function MereniNapoveda({ vysledek, onOpravit, isDark, labelMuted, odsazeni = 36 }) {
    if (!vysledek) return null;
    const barva = { ok: '#10B981', pozor: '#F59E0B', chyba: '#EF4444', info: labelMuted }[vysledek.stav];
    const Ikona = vysledek.stav === 'ok' ? Check : vysledek.stav === 'info' ? null : AlertTriangle;
    const vis = vysledek.vis;
    const drazka = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';

    let pruh = null;
    if (vis?.druh === 'rozmezi') {
        // Osa kolem limitu, ať je značka i mimo pásmo vidět
        const lo = vis.min ?? (vis.max - 10), hi = vis.max ?? (vis.min + 10);
        const pad = Math.max(2, (hi - lo) * 0.6);
        const [od, doo] = [lo - pad, hi + pad];
        const pct = (x) => Math.min(100, Math.max(0, ((x - od) / (doo - od)) * 100));
        pruh = (
            <div style={{ position: 'relative', height: 6, borderRadius: 4, background: 'rgba(239,68,68,0.28)', margin: '6px 0 2px' }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(vis.min ?? od)}%`, right: `${100 - pct(vis.max ?? doo)}%`, background: 'rgba(16,185,129,0.75)', borderRadius: 4 }} />
                {vis.v != null && <div style={{ position: 'absolute', top: -4, width: 3, height: 14, borderRadius: 2, background: 'var(--text-primary)', left: `calc(${pct(vis.v)}% - 1.5px)` }} />}
            </div>
        );
    } else if (vis?.druh === 'zmena') {
        // 0 = minulá hodnota, zelená = běžná změna, oranžová = ještě rozumná, dál překlep
        const osa = vis.max * 1.25 || 1;
        const zmena = (vis.v - vis.od) * vis.smer;
        const pct = (x) => Math.min(100, Math.max(0, (x / osa) * 100));
        pruh = (
            <div style={{ position: 'relative', height: 6, borderRadius: 4, background: 'rgba(239,68,68,0.28)', margin: '6px 0 2px' }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${pct(vis.max)}%`, background: 'rgba(245,158,11,0.55)', borderRadius: 4 }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${pct(vis.bezne != null ? vis.bezne * 2 : vis.max / 2)}%`, background: 'rgba(16,185,129,0.8)', borderRadius: 4 }} />
                {zmena >= 0 && <div style={{ position: 'absolute', top: -4, width: 3, height: 14, borderRadius: 2, background: 'var(--text-primary)', left: `calc(${pct(zmena)}% - 1.5px)` }} />}
            </div>
        );
    }

    return (
        <div style={{ marginLeft: odsazeni, padding: '0 2px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, lineHeight: 1.4, color: vysledek.stav === 'info' ? labelMuted : barva, fontWeight: vysledek.stav === 'info' ? 400 : 600 }}>
                {Ikona && <Ikona size={13} style={{ flexShrink: 0, marginTop: 2 }} />}
                <span>{vysledek.text}</span>
            </div>
            {pruh && <div style={{ maxWidth: 260 }}>{pruh}</div>}
            {vysledek.navrh != null && (
                <button type="button" onClick={() => onOpravit(vysledek.navrh)}
                    style={{ marginTop: 6, padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#06B6D4', color: '#06282E', fontWeight: 700, fontSize: 13 }}>
                    Opravit na {fmt(vysledek.navrh)}
                </button>
            )}
        </div>
    );
}

/**
 * Pole měření v bloku (PhaseModal): číslo s jednotkou a pod ním okamžitá
 * nápověda. Uloží se po dopsání (opuštění pole / Enter), ne při každém úhozu —
 * rozepsané „12" místo „1,2" by jinak u lednice poslalo vedení falešnou odchylku.
 */
export default function MereniPole({ item, odp, checklistId, disabled, onSave, isDark, mutedColor, mimoLimit }) {
    const [val, setVal] = useState(odp.hodnota_cislo != null ? String(odp.hodnota_cislo).replace('.', ',') : '');
    const fokus = useRef(false);
    useEffect(() => {
        if (fokus.current) return;
        setVal(odp.hodnota_cislo != null ? fmt(Number(odp.hodnota_cislo)).replace(/\s/g, '') : '');
    }, [odp.hodnota_cislo]);
    const uloz = (raw) => {
        const v = parseCislo(raw);
        if (Number.isNaN(v)) return;
        const puvodni = odp.hodnota_cislo != null ? Number(odp.hodnota_cislo) : null;
        if (v === puvodni) return;
        onSave({ hodnota_cislo: v });
    };
    const vysledek = vyhodnotMereni(val, item, checklistId);
    const barva = { chyba: '#EF4444', pozor: '#F59E0B', ok: '#10B981' }[vysledek?.stav] || (mimoLimit ? '#EF4444' : null);
    const jednotka = item.mereni_kontext?.jednotka || item.jednotka || item.parametr?.jednotka || '';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {muzeZaporne(item) && (
                    <ZnamenkoTlacitko hodnota={val} disabled={disabled} isDark={isDark} vyska={38}
                        onZmena={n => { setVal(n); if (!fokus.current) uloz(n); }} />
                )}
                <input type="text" inputMode="decimal" autoComplete="off" value={val} disabled={disabled}
                    onFocus={() => { fokus.current = true; }}
                    onChange={e => setVal(e.target.value)}
                    onBlur={e => { fokus.current = false; uloz(e.target.value); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    placeholder="—"
                    style={{
                        width: 140, padding: '8px 12px', borderRadius: 8, fontSize: 17, fontWeight: 700,
                        background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-base)',
                        color: barva || (isDark ? '#fff' : 'var(--text-primary)'),
                        border: `1.5px solid ${barva || (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                        fontVariantNumeric: 'tabular-nums', outline: 'none',
                    }} />
                {jednotka && <span style={{ fontSize: 13, color: mutedColor }}>{jednotka}</span>}
            </div>
            {!disabled && (
                <MereniNapoveda vysledek={vysledek} isDark={isDark} labelMuted={mutedColor} odsazeni={0}
                    onOpravit={(n) => { setVal(fmt(n).replace(/\s/g, '')); uloz(String(n)); }} />
            )}
        </div>
    );
}
